import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { defineStore } from 'pinia';
import { ref, watch, type Ref } from 'vue';
import { usePiniaRefStore } from '../composables/usePiniaRefStore';

export interface Notification {
  id?: string | number;
  title: string;
  message: string;
  extra?: string;
  type?: 'success' | 'error' | 'info' | 'warning' | 'primary' | 'secondary' | 'neutral';
  actions?: {
    icon?: IconDefinition;
    text: string;
    onClick: () => void;
  }[];
  onDismiss?: () => void;
}

interface ActiveNotification extends Notification {
  open: boolean;
}

export const useNotificationStore = defineStore('notifications', () => {
  const notifications = ref<Ref<ActiveNotification>[]>([]);

  const addNotification = (notification: Notification) => {
    const activeNotification = ref<ActiveNotification>({ ...notification, open: true });
    notifications.value.push(activeNotification);

    watch(
      () => activeNotification.value.open,
      (open) => {
        if (!open) {
          const index = notifications.value.indexOf(activeNotification);
          notifications.value.splice(index, 1);
          notification.onDismiss?.();
        }
      },
    );
  };

  const updateNotification = (id: string | number, updatedFields: Partial<ActiveNotification>) => {
    const notificationRef = notifications.value.find((n) => n.value.id === id);
    if (notificationRef) Object.assign(notificationRef.value, updatedFields);
  };

  return { notifications, addNotification, updateNotification };
});

export const useNotifications = () => usePiniaRefStore(useNotificationStore());
